import { PARTIES, PARTY_ORDER, type GlobalAggregate } from '../lib/types';

interface Props {
  aggregate: GlobalAggregate;
}

const nf = new Intl.NumberFormat('sv-SE');

function timeAgo(ms: number): string {
  const sec = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (sec < 60) return `${sec}s sedan`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min sedan`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} tim sedan`;
  return `${Math.floor(h / 24)} d sedan`;
}

export function PulseStrip({ aggregate }: Props) {
  const { totals, recentFlips, mostContested } = aggregate;

  const leader = PARTY_ORDER
    .map(p => ({ p, count: totals.districtsByParty[p] ?? 0 }))
    .sort((a, b) => b.count - a.count)[0];

  const lastFlip = recentFlips[0];
  const hottest = mostContested[0];

  const neutralPct = totals.totalDistricts
    ? (totals.neutralDistricts / totals.totalDistricts) * 100
    : 0;

  const fullyMobilized = totals.fullyMobilizedDistricts ?? 0;

  return (
    <section className="pulse-strip" id="pulsen">
      <h2>Sverige-pulsen</h2>
      <div className="pulse-grid">
        <div className="pulse-tile">
          <div className="pulse-label">Ledande parti</div>
          {leader && leader.count > 0 ? (
            <div className="pulse-value">
              <span
                className="color-dot"
                style={{ background: PARTIES[leader.p].color, marginRight: 8 }}
              />
              {PARTIES[leader.p].name}
              <div className="pulse-sub">{nf.format(leader.count)} distrikt</div>
            </div>
          ) : (
            <div className="pulse-value">—<div className="pulse-sub">Ingen ledare ännu</div></div>
          )}
        </div>

        <div className="pulse-tile">
          <div className="pulse-label">Senaste erövring</div>
          {lastFlip ? (
            <div className="pulse-value">
              <span
                className="color-dot"
                style={{ background: PARTIES[lastFlip.toParty].color, marginRight: 8 }}
              />
              {lastFlip.districtName}
              <div className="pulse-sub">{timeAgo(lastFlip.at.toMillis())}</div>
            </div>
          ) : (
            <div className="pulse-value">—<div className="pulse-sub">Inga erövringar ännu</div></div>
          )}
        </div>

        <div className="pulse-tile">
          <div className="pulse-label">Hetaste striden</div>
          {hottest ? (
            <div className="pulse-value">
              {hottest.districtName}
              <div className="pulse-sub">{nf.format(hottest.population)} invånare</div>
            </div>
          ) : (
            <div className="pulse-value">—<div className="pulse-sub">Allt lugnt</div></div>
          )}
        </div>

        <div className="pulse-tile">
          <div className="pulse-label">Kvar att erövra</div>
          <div className="pulse-value">
            {neutralPct.toFixed(0)} %
            <div className="pulse-sub">{nf.format(totals.neutralDistricts)} neutrala distrikt</div>
          </div>
        </div>

        <div className="pulse-tile">
          <div className="pulse-label">Utan neutrala väljare</div>
          <div className="pulse-value">
            {nf.format(fullyMobilized)}
            <div className="pulse-sub">av {nf.format(totals.totalDistricts)} distrikt</div>
          </div>
        </div>
      </div>
    </section>
  );
}
